import { makeAdvancedReportsApi, makeBenefitsApi } from 'src/http/api/internal/internal-http';
import { ProductRepository } from 'src/repositories/products/products.repository';
import {
  CategoryBulkDeleteResponse,
  CategoryDeletionVerificationResponse,
  CategoryStatus,
  categoryType,
  ProductType,
} from 'src/types/products';
import { create } from 'zustand';

type States = {
  loadingPage: boolean;
  loadingLpProducts: boolean;
  loadingCategoriesList: boolean;
  loadingCategoryDetails: boolean;
  loadingCategoryCreate: boolean;
  loadingCategoryUpdate: boolean;
  loadingCategoriesBulkDelete: boolean;
  loadingCategoriesDeletionVerification: boolean;
  isLoadingChangeLPCProductStatus: boolean;
  products: ProductType[];
  lpProducts: ProductType[];
  partners: any[];
  categories: any[];
  themes: any[];
  stores: any[];
  redeemsFromPartner: any[];
  locations: any[];
  categoriesList: categoryType[];
  categoryDetails: categoryType | null;
};

type Actions = {
  getAll: () => Promise<void>;
  getLpProducts: () => Promise<void>;
  getStores: () => Promise<void>;
  getCategories: () => Promise<void>;
  getThemes: () => Promise<void>;
  getCategoryDetails: (id: string) => Promise<void>;
  getPartners: () => Promise<void>;
  getCategoriesList: () => Promise<void>;
  getRedeemsFromPartner: (partnerId: string) => Promise<void>;
  getLocations: (noAll?: boolean) => Promise<void>;
  create: (product: any) => Promise<boolean>;
  createLPProduct: (product: any) => Promise<boolean>;
  updateLPProduct: (product: any) => Promise<boolean>;
  findById: (id: string, callback: any) => Promise<any>;
  update: (product: any) => Promise<boolean>;
  resetCategoryDetails: () => Promise<void>;
  createCategory: (category: any) => Promise<void>;
  updateCategory: (category: any) => Promise<void>;
  changeCategoryStatus: (categoryId: string, status: CategoryStatus) => Promise<void>;
  deleteCategories: (categoryIds: string[]) => Promise<CategoryBulkDeleteResponse>;
  verifyCategoriesDeletion: (
    categoryIds: string[]
  ) => Promise<CategoryDeletionVerificationResponse>;
  exportData: (ids: string[], userName: string, options?: { isAllSelected?: boolean }) => void;
  approveLPProduct: (id: string) => Promise<boolean>;
  refuseLPProduct: (id: string, comment: string) => Promise<boolean>;
  changeLPProductStatus: (id: string) => Promise<void>;
};

type Store = Actions & States;

const initialValues = {
  products: [],
  lpProducts: [],
  loadingPage: false,
  loadingLpProducts: false,
  loadingCategoriesList: false,
  loadingCategoryDetails: false,
  loadingCategoryUpdate: false,
  loadingCategoriesBulkDelete: false,
  loadingCategoriesDeletionVerification: false,
  loadingCategoryCreate: false,
  isLoadingChangeLPCProductStatus: false,
  partners: [],
  categories: [],
  themes: [],
  stores: [],
  redeemsFromPartner: [],
  locations: [],
  categoriesList: [],
  categoryDetails: null,
};

const repository = new ProductRepository(makeBenefitsApi());
const advancedReportsRepository = new ProductRepository(makeAdvancedReportsApi());

export const useProductsStore = create<Store>((set) => ({
  ...initialValues,

  getAll: async () => {
    set({ loadingPage: true });
    const response = await repository.getAllProducts();
    if (response.isLeft()) {
      set({ loadingPage: false, products: [] });
      return;
    }
    const { value } = response;

    const products = value.map((product: any) => {
      const rewardType = (() => {
        switch (product.rewardType) {
          case 'simple':
            return 'Simples';
          case 'uniqueCode':
            return 'Código Único';
          default:
            return 'Código';
        }
      })();

      return {
        ...product,
        availableQuantity:
          product.availableQuantity || product?.availableCodes?.totalAvailable || 0,
        rewardType,
        totalQuantity:
          product.rewardType === 'simple' ? product.totalQuantity : product.countActiveRedemptions,
        price: product.price,
      };
    });
    set({ products: products as ProductType[], loadingPage: false });
  },

  getLpProducts: async () => {
    set({ loadingLpProducts: true });
    const response = await repository.getLpProducts();
    if (response.isLeft()) {
      set({ loadingLpProducts: false, lpProducts: [] });
      return;
    }
    const { value } = response;

    const lpProducts = value.map((product: any) => {
      const rewardType = (() => {
        switch (product.rewardType) {
          case 'simple':
            return 'Simples';
          case 'uniqueCode':
            return 'Código Único';
          default:
            return 'Código';
        }
      })();

      return {
        ...product,
        availableQuantity:
          product.availableQuantity || product?.availableCodes?.totalAvailable || 0,
        rewardType,
        totalQuantity:
          product.rewardType === 'simple' ? product.totalQuantity : product.countActiveRedemptions,
        price: product.price,
      };
    });
    set({ lpProducts: lpProducts as ProductType[], loadingLpProducts: false });
  },

  getStores: async () => {
    const response = await repository.getStores();
    if (response.isLeft()) {
      set({ stores: [] });
      return;
    }
    const { value } = response;
    const sto = value.map((store: any) => ({ value: store._id, label: store.name }));
    set({
      stores: sto,
    });
  },

  getCategories: async () => {
    const response = await repository.getCategories();
    if (response.isLeft()) {
      set({ categories: [] });
      return;
    }
    const { value } = response;
    const cat = value.map((category: any) => ({ value: category._id, label: category.name }));
    set({
      categories: cat,
    });
  },

  getThemes: async () => {
    const response = await repository.getThemes();
    if (response.isLeft()) {
      set({ themes: [] });
      return;
    }
    const { value } = response;
    const the = value.map((theme: any) => ({ value: theme._id, label: theme.name }));
    set({
      themes: the,
    });
  },

  getCategoriesList: async () => {
    set({ loadingCategoriesList: true });
    const response = await repository.getCategoriesList();
    if (response.isLeft()) {
      set({ categoriesList: [], loadingCategoriesList: false });
      return;
    }

    const { value } = response;
    const categories =
      value?.map((category: categoryType) => ({
        ...category,
      })) || [];

    set({
      categoriesList: categories,
      loadingCategoriesList: false,
    });
  },

  getCategoryDetails: async (id: string) => {
    set({ loadingCategoryDetails: true });
    const response = await repository.getCategoryDetails(id);
    if (response.isLeft()) {
      set({ categoryDetails: null, loadingCategoryDetails: false });
      return;
    }
    set({
      categoryDetails: response.value,
      loadingCategoryDetails: false,
    });
  },

  resetCategoryDetails: async () => set({ categoryDetails: null }),

  updateCategory: async (category: any) => {
    set({ loadingCategoryUpdate: true });
    const response = await repository.updateCategory(category);
    if (response.isLeft()) {
      set({ loadingCategoryUpdate: false });
      return;
    }
    set({
      loadingCategoryUpdate: false,
    });
  },

  changeCategoryStatus: async (categoryId, status) => {
    const response = await repository.changeCategoryStatus(categoryId, status);

    if (response.isLeft()) {
      throw response.value;
    }

    set((state) => ({
      categoriesList: state.categoriesList.map((category) =>
        category._id === categoryId ? { ...category, status } : category
      ),
    }));
  },

  deleteCategories: async (categoryIds) => {
    set({ loadingCategoriesBulkDelete: true });

    try {
      const response = await repository.deleteCategories(categoryIds);

      if (response.isLeft()) {
        throw response.value;
      }

      return response.value;
    } finally {
      set({ loadingCategoriesBulkDelete: false });
    }
  },

  verifyCategoriesDeletion: async (categoryIds) => {
    set({ loadingCategoriesDeletionVerification: true });

    try {
      const response = await repository.verifyCategoriesDeletion(categoryIds);

      if (response.isLeft()) {
        throw response.value;
      }

      return response.value;
    } finally {
      set({ loadingCategoriesDeletionVerification: false });
    }
  },

  createCategory: async (category: any) => {
    set({ loadingCategoryCreate: true });
    const response = await repository.createCategory(category);
    if (response.isLeft()) {
      set({ loadingCategoryCreate: false });
      return;
    }
    set({
      loadingCategoryCreate: false,
    });
  },

  getPartners: async () => {},

  getRedeemsFromPartner: async (partnerId: string) => {},

  getLocations: async (noAll = false) => {
    const response = await repository.getLocations();
    if (response.isLeft()) {
      set({ locations: [] });
      return;
    }
    const { value } = response;
    const loc = value.map((location: any) => ({ value: location._id, label: location.name }));
    if (!noAll) {
      loc.unshift({ value: 'all', label: 'Todos' });
    }
    set({
      locations: loc,
    });
  },

  create: async (product: any) => {
    const response = await repository.createProduct(product);
    return response.isRight();
  },

  createLPProduct: async (product: any) => {
    const response = await repository.createLPProduct(product);
    return response.isRight();
  },

  updateLPProduct: async (product: any) => {
    const response = await repository.updateLPProduct(product);
    return response.isRight();
  },

  findById: async (id: string, callback: any) => {
    const response = await repository.findById(id);
    if (response.isLeft()) {
      return {} as ProductType;
    }
    const { value } = response;
    return value as ProductType;
  },

  update: async (product: any) => {
    const response = await repository.update(product);
    return response.isRight();
  },

  approveLPProduct: async (id: string) => {
    const response = await repository.approveLPProduct(id);
    return response.isRight();
  },

  refuseLPProduct: async (id: string, comment: string) => {
    const response = await repository.refuseLPProduct(id, comment);
    return response.isRight();
  },

  exportData: async (ids: string[], userName: string, options) => {
    // const urlToGet = `${BENEFITS_URL}/benefits/product/export-products`;
    // const query = ids.length ? `?ids=${ids.join(',')}` : '';
    // window.open(urlToGet + query, '_blank');
    await advancedReportsRepository.exportProduct(ids, userName, options);
  },

  changeLPProductStatus: async (id: string) => {
    set({ isLoadingChangeLPCProductStatus: true });
    const response = await repository.changeLPProductStatus(id);
    if (response.isLeft()) {
      set({ isLoadingChangeLPCProductStatus: false });
      return;
    }
    set({ isLoadingChangeLPCProductStatus: false });
  },
}));
